import React, { useState, FormEvent, useEffect } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { useAuth, useAppDispatch } from '../hooks';
import { Button, Input } from '../components/common';
import { profileService } from '../services/profileService';
import './LoginPage.css';

export const LoginPage: React.FC = () => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const { login, isLoading, error, clearError, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useAppDispatch();

  const from = (location.state as any)?.from?.pathname || '/chat';
  const message = (location.state as any)?.message;
  const termsAcceptedDuringSignup = (location.state as any)?.termsAcceptedDuringSignup;

  // Auto-accept terms after login if user accepted during signup
  useEffect(() => {
    const acceptTermsAfterLogin = async () => {
      if (isAuthenticated && termsAcceptedDuringSignup) {
        try {
          await profileService.acceptTerms();
        } catch (err) {
          console.error('Failed to save terms acceptance:', err);
        }
        navigate(from, { replace: true });
      }
    };
    acceptTermsAfterLogin();
  }, [isAuthenticated, termsAcceptedDuringSignup, from, navigate]);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    clearError();
    
    try {
      await login({ username, password });
      // If terms were accepted during signup, the useEffect will handle navigation
      if (!termsAcceptedDuringSignup) {
        navigate(from, { replace: true });
      }
    } catch (err) {
      // Error is handled by Redux
    }
  };

  return (
    <div className="login-page">
      <div className="login-container">
        <div className="login-header">
          <h1 className="login-title">Welcome Back</h1>
          <p className="login-subtitle">
            Sign in to continue to ElevatePhysique AI
          </p>
        </div>

        <form className="login-form" onSubmit={handleSubmit}>
          {message && (
            <div className="login-success">
              {message}
            </div>
          )}
          
          {error && (
            <div className="login-error">
              {error}
            </div>
          )}

          <Input
            label="Username"
            type="text"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            placeholder="Enter your username"
            fullWidth
            required
          />

          <Input
            label="Password"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Enter your password"
            fullWidth
            required
            showPasswordToggle
          />

          <div className="login-options">
            <Link to="/forgot-password" className="forgot-password-link">
              Forgot Password?
            </Link>
          </div>

          <Button
            type="submit"
            variant="primary"
            fullWidth
            isLoading={isLoading}
          >
            Sign In
          </Button>
        </form>

        <p className="login-footer">
          Don't have an account?{' '}
          <Link to="/signup" className="login-link">
            Sign up
          </Link>
        </p>
      </div>
    </div>
  );
};
